import React,{useState, useRef,useEffect} from "react";
import { InpDataList } from "@gull";
import { useTranslate } from 'react-redux-multilingual'
import axios from 'axios'
import SweetAlert from "sweetalert2-react";

// Nombre minimal de caractères (hors espaces de bord) avant de lancer la recherche
const MIN_SEARCH_LENGTH = 3
// Délai de debounce après la dernière frappe (ms)
const SEARCH_DEBOUNCE = 300
// Plafond de résultats demandés au serveur
const SEARCH_LIMIT = 50

/**
 * Hook de recherche client côté serveur.
 * Chaque recherche porte sur le terme complet saisi ; le serveur renvoie les meilleurs résultats
 * (plafonnés) et le total, ce qui évite de charger/afficher des milliers de lignes.
 */
const useSearchClients = () => {
    const [loadingSearchClient,setLoadingSearchClient]=useState(false)
    const [generalErrorSearchClient,setGeneralErrorSearchClient]=useState(false)
    const [datas,setDatas]=useState([])
    const [totalResults,setTotalResults]=useState(0)
    const searchInputClient = useRef('')
    // Identifiant de la dernière requête : permet d'ignorer les réponses arrivées en retard
    const lastRequestId = useRef(0)

    /**
     * Interroge l'API avec le terme complet et met à jour la liste affichée.
     */
    const loadClients = async (toSearch) => {
        const requestId = ++lastRequestId.current
        setLoadingSearchClient(true)
        await axios.get(process.env.REACT_APP_API_URL+"api/client/name/"+encodeURIComponent(toSearch)+"?limit="+SEARCH_LIMIT
        ).then((response) => {
            if(requestId!==lastRequestId.current) return;
            setDatas(response.data.datas.map((value)=>({
                id: value.id,
                name: value.name+' '+value.firstname+' - '+value.email+' - '+value.tel
            })))
            setTotalResults(response.data.total ?? response.data.datas.length)
            setLoadingSearchClient(false)
        }).catch((error) => {
            if(error=='end_session') return;
            if(requestId!==lastRequestId.current) return;
            setLoadingSearchClient(false)
            setGeneralErrorSearchClient(true)
        })
    }

    /**
     * Vide la liste et invalide toute requête en cours (saisie trop courte ou composant démonté).
     */
    const resetSearch = () => {
        lastRequestId.current++
        setDatas([])
        setTotalResults(0)
        setLoadingSearchClient(false)
    }

    return [loadingSearchClient,loadClients,generalErrorSearchClient,setGeneralErrorSearchClient,datas,totalResults,resetSearch,searchInputClient]
}

const InputSearchClient = (props) =>{
    const t =useTranslate()
    const [loadingSearchClient,loadClients,generalErrorSearchClient,setGeneralErrorSearchClient,datas,totalResults,resetSearch,searchInputClient]=useSearchClients()

    const [clientName,setClientName] = useState('')
    const [lastSelectedName,setLastSelectedName] = useState('')
    const [noClientToEdit,setNoClientToEdit] = useState(false)

    const dataListDropClient = useRef('')
    const debounceTimer = useRef(null)

    // Annule le debounce en attente et invalide les requêtes en cours au démontage
    useEffect(function () {
        return () => {
            clearTimeout(debounceTimer.current)
            resetSearch()
        }
    }, [])

    const toggleAlertSearchClient = () => {
        setGeneralErrorSearchClient(false)
    }

    const toggleNoClientToEdit = () => {
        setNoClientToEdit(false)
    }

    //Utilisé uniquement sur la création de facture/devis dans le module facturation pour renseigner le nom du nouveau dans cette input
    useEffect(function () {
        if(props.from=='facturation' && props.clientDetail && Object.keys(props.clientDetail).length==0){
            if (props.clientName) setClientName(props.clientName)
            else setClientName('')
        }else if(props.from=='facturation' && props.clientDetail && Object.keys(props.clientDetail).length!=0){
            let fullName = props.clientDetail.name+' '+props.clientDetail.firstname+' - '+props.clientDetail.email+' - '+props.clientDetail.tel
            setClientName(fullName)
            setLastSelectedName(fullName)
        }else if(props.changeDossierClient){
            //Exécuté depuis le détail d'un document pour afficher le nom de client
            setClientName(props.changeDossierClient.name+' '+props.changeDossierClient.firstname+' - '+props.changeDossierClient.email+' - '+props.changeDossierClient.tel)
        }
    }, [props.clientDetail])

    useEffect(function () {
        if(props.defaultClientName) setClientName(props.defaultClientName)
    }, [props.defaultClientName])

    /**
     * Gère la saisie : la recherche part (après debounce) sur le terme complet, espaces de bord ignorés,
     * pour que "le " ne déclenche pas une requête quasi non filtrée.
     */
    const handleSearch = (e) => {
        const value = e.target.value
        setClientName(value)
        if (props.setClientName) props.setClientName(value)

        clearTimeout(debounceTimer.current)
        const term = value.trim()
        if(term.length>=MIN_SEARCH_LENGTH){
            dataListDropClient.current.classList.remove("d-none");
            debounceTimer.current = setTimeout(() => loadClients(term), SEARCH_DEBOUNCE)
        }else{
            dataListDropClient.current.classList.add("d-none");
            resetSearch()
        }
    }

    const handleCLick = (id,valeur) => {
        props.setClient(id)
        setClientName(valeur)
        setLastSelectedName(valeur)
        if (props.setClientName) props.setClientName(valeur)
        if(props.setClientInfo) props.setClientInfo(valeur)
        dataListDropClient.current.classList.add("d-none");
    }

    const handleBlur = () => {
        if(clientName==''){
            if(props.keepSelectionOnClear && lastSelectedName!=''){
                setClientName(lastSelectedName)
            }else{
                props.setClient(false)
            }
        }
        dataListDropClient.current.classList.add("d-none");
    }

    const handleFocus = () => {
        searchInputClient.current.select();
    }

    return(
        <>
            <div className="position-relative">
                <InpDataList
                    placeholder={t('customerPlacehlder')}
                    value={clientName}
                    onClick={handleCLick}
                    onChange={handleSearch}
                    datas={datas}
                    declenchement={MIN_SEARCH_LENGTH}
                    footer={(totalResults>datas.length) ? datas.length+' / '+totalResults+' - '+t('refineSearchResults') : false}
                    listRef={dataListDropClient}
                    inputRef={searchInputClient}
                    onBlur={handleBlur}
                    disabled={props.disabled || props.changeDossierClient}
                    inputName="searchClient"
                    onFocus={handleFocus}
                />
                {(loadingSearchClient) ?
                    <span><span>&nbsp;</span><div className="spinner-border spinner-border-sm topBarLeftPosition2"></div></span>
                :
                    false
                }
            </div>
            {(generalErrorSearchClient) &&
                <SweetAlert show="true" title={t('unknowError')} type="error" onConfirm={() => toggleAlertSearchClient()}/>
            }
            {(noClientToEdit) &&
                    <SweetAlert show="true" title={t('noClientToEdit')} type="error" onConfirm={() => toggleNoClientToEdit()}/>
            }
        </>
    )
}

export default InputSearchClient;